import React from 'react';

import {
  BannerSection,
  CampaignsSection,
  CardsSection,
  HeroSection,
  ListSection,
  PeopleSection,
  PhotosSection,
  StarsSection,
  StatsSection,
  TextSection,
  TwoColumnSection,
} from './WelcomeSections';

import type { WelcomePageContent, WelcomeSection, WelcomeStar } from '@utazzo/shared';

type Media = Record<string, string>;

/** One section by type, or null when it has nothing to show. */
const renderSection = (
  s: WelcomeSection,
  websiteStars: WelcomeStar[],
  media: Media,
): React.ReactNode => {
  switch (s.type) {
    case 'hero':
      return s.imageMediaId || s.mobileImageMediaId || s.titleTop || s.titleBottom ? (
        <HeroSection hero={s} media={media} />
      ) : null;
    case 'text':
      return s.heading || s.body ? <TextSection story={s} /> : null;
    case 'twoColumn':
      return s.left || s.right ? <TwoColumnSection vm={s} /> : null;
    case 'photos':
      return s.heading || s.body || s.photoMediaIds.length > 0 ? (
        <PhotosSection journey={s} media={media} />
      ) : null;
    case 'people':
      return s.items.length > 0 ? <PeopleSection founders={s} media={media} /> : null;
    case 'campaigns':
      return <CampaignsSection campaigns={s} media={media} />;
    case 'stats':
      return s.items.length > 0 ? <StatsSection stats={s} /> : null;
    case 'cards':
      return s.items.length > 0 ? <CardsSection values={s} media={media} /> : null;
    case 'stars': {
      const items = s.source === 'custom' ? s.items : websiteStars;
      return items.length > 0 ? <StarsSection stars={s} items={items} media={media} /> : null;
    }
    case 'list':
      return s.items.length > 0 ? <ListSection section={s} /> : null;
    case 'banner':
      return s.title || s.eyebrow ? <BannerSection closing={s} media={media} /> : null;
  }
};

/**
 * The page's sections in the admin-chosen order. Shared by the live page and
 * the editor preview so both render identically. Hidden or empty sections
 * are skipped entirely (no leftover spacing).
 */
export const WelcomeContent = ({
  content,
  websiteStars,
  media,
}: {
  content: WelcomePageContent;
  websiteStars: WelcomeStar[];
  media: Media;
}): React.JSX.Element => (
  <div className="@container space-y-10 pb-6 sm:space-y-14">
    {content.sections
      .filter((s) => s.visible)
      .map((s) => {
        const node = renderSection(s, websiteStars, media);
        return node ? <React.Fragment key={s.id}>{node}</React.Fragment> : null;
      })}
  </div>
);
